import { useState } from 'react'
import {
  Database, Server, Layers, Network, Key,
  Search, RefreshCw, Download,
  CheckCircle2, AlertTriangle, Box,
  Tag, Link2, Eye,
} from 'lucide-react'
import styles from './CMDBPage.module.css'

type CIType = 'all' | 'cluster' | 'server' | 'application' | 'network' | 'certificate' | 'database'

interface ConfigItem {
  id: string
  name: string
  type: Exclude<CIType, 'all'>
  environment: string
  status: 'healthy' | 'degraded' | 'unknown'
  owner: string
  version: string
  location: string
  lastUpdated: string
  relations: number
  tags: string[]
}

const MOCK_CIS: ConfigItem[] = [
  { id: 'CI-001', name: 'aks-acme-production',          type: 'cluster',     environment: 'production', status: 'healthy',  owner: 'infra-team',    version: '1.29.4',  location: 'eastus',   lastUpdated: '2026-04-08', relations: 14, tags: ['aks','azure','prod']           },
  { id: 'CI-002', name: 'aks-acme-demo',                 type: 'cluster',     environment: 'demo',       status: 'healthy',  owner: 'infra-team',    version: '1.29.2',  location: 'eastus',   lastUpdated: '2026-04-07', relations: 9,  tags: ['aks','azure','demo']          },
  { id: 'CI-003', name: 'aks-acme-dev',                 type: 'cluster',     environment: 'dev',        status: 'degraded', owner: 'infra-team',    version: '1.28.5',  location: 'eastus2',  lastUpdated: '2026-04-06', relations: 7,  tags: ['aks','azure','dev']            },
  { id: 'CI-004', name: 'appcentral-prod',              type: 'application', environment: 'production', status: 'healthy',  owner: 'platform-team', version: '2026.04', location: 'eastus',   lastUpdated: '2026-04-08', relations: 6,  tags: ['appcentral','django']         },
  { id: 'CI-005', name: 'aip-platform-prod',           type: 'application', environment: 'production', status: 'healthy',  owner: 'platform-team', version: '5.2.1',   location: 'eastus',   lastUpdated: '2026-04-05', relations: 8,  tags: ['aip','ai']                    },
  { id: 'CI-006', name: 'postgres-prod-flex',           type: 'database',    environment: 'production', status: 'healthy',  owner: 'dba-team',      version: '17.0',    location: 'eastus',   lastUpdated: '2026-04-08', relations: 12, tags: ['postgresql','azure','flex']   },
  { id: 'CI-007', name: 'postgres-demo-flex',            type: 'database',    environment: 'demo',       status: 'healthy',  owner: 'dba-team',      version: '17.0',    location: 'eastus',   lastUpdated: '2026-04-03', relations: 7,  tags: ['postgresql','azure']          },
  { id: 'CI-008', name: 'haproxy-prod',                 type: 'network',     environment: 'production', status: 'healthy',  owner: 'infra-team',    version: '2.8.3',   location: 'eastus',   lastUpdated: '2026-04-07', relations: 24, tags: ['haproxy','lb','ssl']          },
  { id: 'CI-009', name: 'vnet-acme-prod',               type: 'network',     environment: 'production', status: 'healthy',  owner: 'infra-team',    version: 'N/A',     location: 'eastus',   lastUpdated: '2026-03-20', relations: 5,  tags: ['vnet','azure','network']      },
  { id: 'CI-010', name: 'ssl-acme-aptean-app',          type: 'certificate', environment: 'production', status: 'healthy',  owner: 'infra-team',    version: 'v3',      location: 'global',   lastUpdated: '2026-04-01', relations: 8,  tags: ['ssl','cloudflare','tls13']    },
  { id: 'CI-011', name: 'ssl-api-aptean-app',           type: 'certificate', environment: 'production', status: 'degraded', owner: 'infra-team',    version: 'v3',      location: 'global',   lastUpdated: '2026-02-01', relations: 3,  tags: ['ssl','expiring']              },
  { id: 'CI-012', name: 'redis-prod',                   type: 'server',      environment: 'production', status: 'healthy',  owner: 'platform-team', version: '7.2.4',   location: 'eastus',   lastUpdated: '2026-04-05', relations: 4,  tags: ['redis','cache','azure']       },
  { id: 'CI-013', name: 'pay-edi-prod',                 type: 'application', environment: 'production', status: 'healthy',  owner: 'platform-team', version: '3.1.0',   location: 'eastus2',  lastUpdated: '2026-04-04', relations: 5,  tags: ['pay-edi','finance']           },
  { id: 'CI-014', name: 'agent-central-prod',           type: 'application', environment: 'production', status: 'unknown',  owner: 'platform-team', version: '1.2.0',   location: 'eastus',   lastUpdated: '2026-04-02', relations: 3,  tags: ['agents','ai','new']           },
  { id: 'CI-015', name: 'mcp-server-prod',              type: 'server',      environment: 'production', status: 'healthy',  owner: 'platform-team', version: '2.1.0',   location: 'eastus',   lastUpdated: '2026-04-08', relations: 6,  tags: ['mcp','ai','claude']           },
]

const CI_TYPE_ICONS: Record<Exclude<CIType, 'all'>, React.ElementType> = {
  cluster:     Layers,
  server:      Server,
  application: Box,
  network:     Network,
  certificate: Key,
  database:    Database,
}

const CI_TYPE_COLORS: Record<Exclude<CIType, 'all'>, string> = {
  cluster:     'var(--dc-deploy)',
  server:      'var(--dc-infra)',
  application: 'var(--aptean-teal)',
  network:     'var(--dc-ops)',
  certificate: 'var(--dc-infra)',
  database:    'var(--dc-observe)',
}

const STATUS_CONFIG = {
  healthy:  { label: 'Healthy',  cls: 'healthy'  },
  degraded: { label: 'Degraded', cls: 'degraded' },
  unknown:  { label: 'Unknown',  cls: 'unknown'  },
}

const CI_TYPES: { id: CIType; label: string }[] = [
  { id: 'all',         label: 'All CIs'      },
  { id: 'cluster',     label: 'Clusters'     },
  { id: 'application', label: 'Applications' },
  { id: 'database',    label: 'Databases'    },
  { id: 'network',     label: 'Network'      },
  { id: 'certificate', label: 'Certificates' },
  { id: 'server',      label: 'Servers'      },
]

const STATS = [
  { icon: Database,      label: 'Total CIs',       value: '248',  delta: '+12 this week', positive: true  },
  { icon: CheckCircle2,  label: 'Healthy',          value: '231',  delta: '93%',           positive: true  },
  { icon: AlertTriangle, label: 'Degraded / Unknown',value: '17', delta: 'Needs attention',positive: false },
  { icon: Link2,         label: 'Total Relations',  value: '1,892',delta: 'CI dependencies',positive: true  },
]

export function CMDBPage() {
  const [activeType, setActiveType] = useState<CIType>('all')
  const [search, setSearch] = useState('')
  const [selectedCI, setSelectedCI] = useState<ConfigItem | null>(null)

  const filtered = MOCK_CIS.filter(ci => {
    const matchType   = activeType === 'all' || ci.type === activeType
    const matchSearch = !search
      || ci.name.toLowerCase().includes(search.toLowerCase())
      || ci.id.toLowerCase().includes(search.toLowerCase())
      || ci.tags.some(t => t.includes(search.toLowerCase()))
    return matchType && matchSearch
  })

  return (
    <div className={styles.page}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <Database size={22} />
          </div>
          <div>
            <h1 className={styles.headerTitle}>CMDB</h1>
            <p className={styles.headerDesc}>Configuration Management Database — infrastructure inventory and dependency mapping</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnIcon} title="Refresh"><RefreshCw size={15} /></button>
          <button className={styles.btnSecondary}><Download size={14} /> Export</button>
          <button className={styles.btnPrimary}>+ Register CI</button>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className={styles.statsRow}>
        {STATS.map(s => {
          const Icon = s.icon
          return (
            <div key={s.label} className={styles.statCard}>
              <div className={styles.statIcon}><Icon size={16} /></div>
              <div>
                <div className={styles.statValue}>{s.value}</div>
                <div className={styles.statLabel}>{s.label}</div>
                <div className={`${styles.statDelta} ${s.positive ? styles.deltaPos : styles.deltaNeg}`}>
                  {s.delta}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* ── Controls ── */}
      <div className={styles.controls}>
        <div className={styles.typeFilters}>
          {CI_TYPES.map(t => (
            <button
              key={t.id}
              className={`${styles.typeBtn} ${activeType === t.id ? styles.typeBtnActive : ''}`}
              onClick={() => setActiveType(t.id)}
            >
              {t.label}
              {t.id !== 'all' && (
                <span className={styles.typeCount}>
                  {MOCK_CIS.filter(ci => ci.type === t.id).length}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className={styles.searchBox}>
          <Search size={13} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search name, ID, or tag..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* ── Main layout ── */}
      <div className={`${styles.mainLayout} ${selectedCI ? styles.splitLayout : ''}`}>
        {/* CI Table */}
        <div className={styles.tableWrap}>
          <div className={styles.tableHeader}>
            <span>CI</span>
            <span>Type</span>
            <span>Environment</span>
            <span>Version</span>
            <span>Owner</span>
            <span>Status</span>
            <span>Relations</span>
            <span>Updated</span>
          </div>

          {filtered.length === 0 ? (
            <div className={styles.emptyState}>
              <Database size={32} style={{ opacity: 0.2 }} />
              <div>No configuration items match your filters</div>
            </div>
          ) : filtered.map(ci => {
            const Icon = CI_TYPE_ICONS[ci.type]
            const sc   = STATUS_CONFIG[ci.status]
            const isSelected = selectedCI?.id === ci.id
            return (
              <div
                key={ci.id}
                className={`${styles.tableRow} ${isSelected ? styles.tableRowSelected : ''}`}
                onClick={() => setSelectedCI(isSelected ? null : ci)}
              >
                <div className={styles.ciName}>
                  <div
                    className={styles.ciTypeIcon}
                    style={{ color: CI_TYPE_COLORS[ci.type], background: `color-mix(in srgb, ${CI_TYPE_COLORS[ci.type]} 12%, transparent)` }}
                  >
                    <Icon size={13} />
                  </div>
                  <div>
                    <div className={styles.ciNameText}>{ci.name}</div>
                    <div className={styles.ciId}>{ci.id}</div>
                  </div>
                </div>
                <span className={styles.ciType}>{ci.type}</span>
                <span className={styles.ciEnv}>{ci.environment}</span>
                <span className={styles.ciVersion}>{ci.version}</span>
                <span className={styles.ciOwner}>{ci.owner}</span>
                <span className={`${styles.ciStatus} ${styles[sc.cls]}`}>
                  <span className={styles.statusDot} />
                  {sc.label}
                </span>
                <span className={styles.ciRelations}>
                  <Link2 size={12} />
                  {ci.relations}
                </span>
                <span className={styles.ciDate}>{ci.lastUpdated}</span>
              </div>
            )
          })}
        </div>

        {/* Detail Panel */}
        {selectedCI && (
          <div className={styles.detailPanel}>
            <div className={styles.detailHeader}>
              <div className={styles.detailTitle}>{selectedCI.name}</div>
              <button className={styles.detailClose} onClick={() => setSelectedCI(null)}>✕</button>
            </div>

            <div className={styles.detailBody}>
              <div className={styles.detailSection}>
                <div className={styles.detailSectionTitle}>Identity</div>
                {[
                  { label: 'CI ID',          value: selectedCI.id          },
                  { label: 'Type',           value: selectedCI.type        },
                  { label: 'Environment',    value: selectedCI.environment },
                  { label: 'Version',        value: selectedCI.version     },
                  { label: 'Owner',          value: selectedCI.owner       },
                  { label: 'Location',       value: selectedCI.location    },
                  { label: 'Last Updated',   value: selectedCI.lastUpdated },
                ].map(row => (
                  <div key={row.label} className={styles.detailRow}>
                    <span className={styles.detailLabel}>{row.label}</span>
                    <span className={styles.detailValue}>{row.value}</span>
                  </div>
                ))}
              </div>

              <div className={styles.detailSection}>
                <div className={styles.detailSectionTitle}>Tags</div>
                <div className={styles.tagList}>
                  {selectedCI.tags.map(t => (
                    <span key={t} className={styles.tag}>
                      <Tag size={10} />
                      {t}
                    </span>
                  ))}
                </div>
              </div>

              <div className={styles.detailSection}>
                <div className={styles.detailSectionTitle}>
                  Dependencies ({selectedCI.relations})
                </div>
                <div className={styles.relationsPlaceholder}>
                  <Link2 size={20} style={{ opacity: 0.3 }} />
                  <span>Dependency graph available in full view</span>
                  <button className={styles.btnSecondary} style={{ marginTop: 8 }}>
                    <Eye size={13} /> View Graph
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
